import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Camera } from "lucide-react";
import { MaskLine } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1561501900-3701fa6a0864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBzdW5zZXR8ZW58MHx8fHwxNzg0ODk4MTQwfDA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onJoin }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* Left — kinetic headline */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-label text-[#F97316] mb-6"
          >
            <Camera className="w-4 h-4" /> Programme photographes partenaires
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[1.02] text-[#0A0A0A]">
            <MaskLine delay={0.15}>Vendez vos photos</MaskLine>
            <MaskLine delay={0.28}>de resort.</MaskLine>
            <MaskLine delay={0.42} className="text-[#F97316]">Gardez 80 %</MaskLine>
            <MaskLine delay={0.54} className="text-[#F97316]">de vos ventes.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 text-base md:text-lg text-[#52525B] font-light max-w-md leading-relaxed"
          >
            Rejoignez le réseau iFotoLife, mettez vos clichés en vente auprès de milliers de vacanciers, et laissez-nous gérer le paiement et la logistique.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={onJoin}
              className="group inline-flex items-center justify-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold px-7 py-4 rounded-full transition-all duration-300"
              data-testid="hero-cta-button"
            >
              Devenir partenaire
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href="https://business.ifotolife.shop"
              className="inline-flex items-center justify-center gap-2 border border-[#E4E4E7] hover:border-[#0A0A0A] text-[#0A0A0A] font-semibold px-7 py-4 rounded-full transition-colors duration-300"
              data-testid="hero-secondary-cta"
            >
              Comment ça marche
            </a>
          </motion.div>
        </div>

        {/* Right — treated photography with parallax */}
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[4/5] sm:aspect-[16/12] overflow-hidden"
          >
            <motion.img
              src={HERO_IMG}
              alt="Resort de luxe au coucher du soleil"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[118%] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
            {/* floating stat chip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md px-5 py-3 border border-white/60"
            >
              <div className="text-2xl font-black text-[#F97316] tracking-tight">1 800 €</div>
              <div className="text-[10px] font-black uppercase tracking-label text-[#52525B]">Revenu moyen / mois</div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
